import { useEffect } from 'react'
import posthog from 'posthog-js'
import { router } from '@inertiajs/react'

interface PostHogConfig {
  apiKey: string
  apiHost: string
  enabled: boolean
}

interface PostHogProviderProps {
  config?: PostHogConfig | null
  user?: {
    id: number
    email: string
    name: string
    firstName: string
    lastName: string
  } | null
  children: React.ReactNode
}

export function PostHogProvider({ config, user, children }: PostHogProviderProps) {
  // Initialize PostHog with enhanced tracking - ONLY for logged-in users
  useEffect(() => {
    // Only track logged-in users
    if (!user) {
      // If no user, ensure PostHog is disabled
      if (posthog.__loaded) {
        posthog.reset()
      }
      return
    }

    // Only initialize if PostHog is enabled and we have the required config
    if (config && config.enabled && config.apiKey && config.apiHost) {
      // Check if PostHog is already initialized
      if (!posthog.__loaded) {
        posthog.init(config.apiKey, {
          api_host: config.apiHost,
          person_profiles: 'identified_only',

          // Pageview tracking
          capture_pageview: false, // We'll manually capture pageviews with Inertia
          capture_pageleave: true,

          // Automatic event capture
          autocapture: {
            dom_event_allowlist: ['click', 'change', 'submit'], // Capture clicks, form changes, and submissions
            url_allowlist: undefined, // Track all URLs
            element_allowlist: ['button', 'a', 'input', 'select', 'textarea', 'form'], // Track these elements
            css_selector_allowlist: undefined, // Track all elements
          },

          // Session recording (optional - can be disabled for privacy)
          disable_session_recording: false,
          session_recording: {
            recordCrossOriginIframes: false,
            maskAllInputs: true, // Mask sensitive input fields
            maskInputOptions: {
              password: true,
            },
          },

          // Performance tracking
          capture_performance: true,

          // Additional properties to capture
          property_denylist: [], // Don't capture these properties

          // Enable for better debugging in development
          loaded: (posthog) => {
            if (import.meta.env.DEV) {
              posthog.debug()
            }
          },
        })
      }

      // Identify user
      posthog.identify(
        user.id.toString(),
        {
          email: user.email,
          name: user.name,
          first_name: user.firstName,
          last_name: user.lastName,
        }
      )

      // Set user properties for filtering in PostHog
      posthog.people.set({
        email: user.email,
        name: user.name,
        first_name: user.firstName,
        last_name: user.lastName,
      })
    }
  }, [config, user])

  // Capture pageview on mount and on Inertia navigation - ONLY for logged-in users
  useEffect(() => {
    if (!user || !config || !config.enabled) return

    // Capture initial pageview
    posthog.capture('$pageview')

    // Listen to Inertia navigation events to capture pageviews
    const handleNavigate = () => {
      posthog.capture('$pageview')
    }

    // Inertia emits 'navigate' event on every navigation
    router.on('navigate', handleNavigate)

    // Cleanup
    return () => {
      router.on('navigate', () => {})
    }
  }, [config, user])

  // Track errors automatically - ONLY for logged-in users
  useEffect(() => {
    if (!user || !config || !config.enabled) return

    const handleError = (event: ErrorEvent) => {
      posthog.capture('error_occurred', {
        error_message: event.message,
        error_stack: event.error?.stack,
        error_filename: event.filename,
        error_line: event.lineno,
        error_column: event.colno,
      })
    }

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      posthog.capture('unhandled_rejection', {
        reason: event.reason,
      })
    }

    window.addEventListener('error', handleError)
    window.addEventListener('unhandledrejection', handleUnhandledRejection)

    return () => {
      window.removeEventListener('error', handleError)
      window.removeEventListener('unhandledrejection', handleUnhandledRejection)
    }
  }, [config, user])

  return <>{children}</>
}
